import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
    Table
} from 'reactstrap';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { useLocation, useNavigate } from 'react-router-dom';
import { supplierActions } from '../../sagas/supplierSlice';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import { formatPrice, preventScroll } from '../../helpers/utility';
import invoiceSupplierService from '../../services/invoiceSupplierService';
import * as receiptNoteService from '../../services/receiptNoteService';
import retourFournisseurService from '../../services/retourFournisseurService';
import './ReglementForm.css';

const unwrapList = (response) => {
    const data = response?.data ?? response;
    const body = data?.body ?? data;
    return Array.isArray(body) ? body : (body?.content || body?.data || []);
};

const toNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

const ReglementFournisseurForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { loading } = useSelector(state => state.reglementsFournisseur);
    const { suppliers } = useSelector(state => state.suppliers);
    const reglement = location.state?.reglement || null;
    const canEdit = (reglement && reglement.statut === 'EN_ATTENTE') || !reglement;

    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [typeReglement, setTypeReglement] = useState(reglement?.typeReglement || 'FACTURE');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [availableRetours, setAvailableRetours] = useState([]);
    const [selectedRetours, setSelectedRetours] = useState(reglement?.utilisationRetours || []);
    const [loadingRetours, setLoadingRetours] = useState(false);
    const formikRef = useRef(null);
    const restoredDocuments = useRef(false);
    const [documentsError, setDocumentsError] = useState('');
    const [retoursError, setRetoursError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [documentQuery, setDocumentQuery] = useState('');
    const listPath = location.pathname.startsWith('/finance-module')
        ? '/finance-module/payments/fournisseur' : '/purchases-module/payments/fournisseur';

    const fournisseurOptions = useMemo(() => (suppliers || []).map(fournisseur => ({
        value: fournisseur.id,
        label: (fournisseur.entreprise || `${fournisseur.nom || ''} ${fournisseur.prenom || ''}`.trim() || fournisseur.code || 'Fournisseur') + (fournisseur.code ? ` (${fournisseur.code})` : ''),
        solde: fournisseur.solde,
        totalDebit: fournisseur.totalDebit,
        totalCredit: fournisseur.totalCredit,
    })), [suppliers]);

    const modeReglementOptions = [
        { value: 'ESPECES', label: 'Espèces' },
        { value: 'CHEQUE', label: 'Chèque' },
        { value: 'VIREMENT', label: 'Virement' },
        { value: 'CARTE_BANCAIRE', label: 'Carte bancaire' },
    ];

    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        fournisseurId: reglement?.fournisseurId || '',
        numFactures: reglement?.numFactures || '',
        numBonReceptions: reglement?.numBonReceptions || '',
        typeReglement: reglement?.typeReglement || 'FACTURE',
        montantRegle: reglement?.montantRegle || 0,
        montantPaiementReel: reglement?.montantPaiementReel ?? reglement?.montantRegle ?? 0,
        montantRetoursUtilises: reglement?.montantRetoursUtilises || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        commentaire: reglement?.commentaire || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
    };

    const validationSchema = Yup.object().shape({
        fournisseurId: Yup.number().typeError('Choisissez un fournisseur.').required('Choisissez un fournisseur.'),
        dateReglement: Yup.date().typeError('Entrez une date valide.').required('Entrez la date du paiement.'),
        modeReglement: Yup.string().required('Choisissez le moyen de paiement.'),
    });

    const getDocumentFournisseurId = (doc) => doc.fournisseurId || doc.fournisseur?.id;
    const getDocumentFournisseurName = (doc) => doc.fournisseurNom || doc.nomFournisseur || doc.fournisseurName || doc.fournisseur?.entreprise || '-';
    const getDocumentDate = (doc) => doc.dateFacture || doc.dateReception || doc.dateOperation || doc.createdAt || '-';
    const getDocumentTotal = (doc) => toNumber(doc.montantTotalTTC ?? doc.montantTTC ?? doc.totalTTC);
    const getDocumentPaid = (doc) => toNumber(doc.montantRegle ?? doc.montantPaye ?? doc.montantPayeTTC);
    const getDocumentRemaining = (doc) => Math.max(toNumber(doc.montantRestant ?? (getDocumentTotal(doc) - getDocumentPaid(doc))), 0);

    const selectedTotal = useMemo(
        () => !canEdit ? toNumber(reglement?.montantRegle) : selectedDocuments.reduce((sum, doc) => sum + getDocumentRemaining(doc), 0),
        [selectedDocuments, canEdit, reglement]
    );
    const retoursTotal = useMemo(
        () => !canEdit ? toNumber(reglement?.montantRetoursUtilises) : selectedRetours.reduce((sum, retour) => sum + toNumber(retour.montantUtilise), 0),
        [selectedRetours, canEdit, reglement]
    );
    const amountToPay = !canEdit ? toNumber(reglement?.montantPaiementReel ?? selectedTotal - retoursTotal) : Math.max(selectedTotal - retoursTotal, 0);

    const filteredDocuments = useMemo(() => {
        return documents
            .filter(doc => getDocumentRemaining(doc) > 0)
            .filter(doc => !documentQuery || String(doc.numero || '').toLowerCase().includes(documentQuery.toLowerCase()))
            .filter(doc => !selectedFournisseur || String(getDocumentFournisseurId(doc)) === String(selectedFournisseur.value));
    }, [documents, selectedFournisseur, documentQuery]);

    const applyReturns = (total, retours, setFieldValue) => {
        let remaining = total;
        const next = retours
            .filter(retour => !retour.statut || retour.statut === 'VALIDE')
            .map(retour => {
                const available = Math.max(toNumber(retour.montantDisponible ?? retour.montantTTC), 0);
                const montantUtilise = Math.min(available, remaining);
                remaining = Math.max(remaining - montantUtilise, 0);
                return {
                    retourId: retour.id,
                    numeroRetour: retour.numeroRetour,
                    montantRetour: retour.montantTTC,
                    montantDisponible: available,
                    montantUtilise,
                    dateRetour: retour.dateOperation,
                };
            })
            .filter(retour => retour.montantUtilise > 0);

        const used = next.reduce((sum, retour) => sum + retour.montantUtilise, 0);
        setSelectedRetours(next);
        setFieldValue('montantRegle', total);
        setFieldValue('montantRetoursUtilises', used);
        setFieldValue('montantPaiementReel', Math.max(total - used, 0));
    };

    useEffect(() => {
        dispatch(supplierActions.search({ query: '' }));
    }, [dispatch]);

    useEffect(() => {
        if (!reglement) return;
        const fournisseur = fournisseurOptions.find(option => String(option.value) === String(reglement.fournisseurId));
        if (fournisseur) setSelectedFournisseur(fournisseur);
    }, [reglement, fournisseurOptions]);

    useEffect(() => {
        if (!canEdit) return;
        let cancelled = false;
        setDocumentsLoading(true);
        setDocumentsError('');
        const request = typeReglement === 'FACTURE'
            ? invoiceSupplierService.search({ fournisseurId: selectedFournisseur?.value })
            : receiptNoteService.search({ fournisseurId: selectedFournisseur?.value, paymentStatus: 'IMPAYE' });
        Promise.resolve(request).then(response => {
            if (cancelled) return;
            const next = unwrapList(response);
            setDocuments(next);
            if (reglement && !restoredDocuments.current && selectedFournisseur) {
                const numbers = String(reglement.numFactures || reglement.numBonReceptions || '').split(',').map(number => number.trim());
                setSelectedDocuments(next.filter(doc => (reglement.documentsIds || []).includes(doc.id) || numbers.includes(doc.numero)));
                restoredDocuments.current = true;
            }
        }).catch(() => {
            if (!cancelled) { setDocuments([]); setDocumentsError('Les documents ne sont pas chargés. Réessayez.'); }
        }).finally(() => { if (!cancelled) setDocumentsLoading(false); });
        return () => { cancelled = true; };
    }, [typeReglement, selectedFournisseur?.value, canEdit, reglement, reloadKey]);

    useEffect(() => {
        if (!canEdit || !selectedFournisseur) {
            setAvailableRetours([]);
            return;
        }
        let cancelled = false;
        setAvailableRetours([]);
        setLoadingRetours(true);
        setRetoursError('');
        retourFournisseurService.availableCredits(selectedFournisseur.value)
            .then(response => { if (!cancelled) setAvailableRetours(unwrapList(response).filter(retour => !retour.statut || retour.statut === 'VALIDE')); })
            .catch(() => { if (!cancelled) setRetoursError('Les retours ne sont pas chargés. Réessayez avant d’enregistrer.'); })
            .finally(() => { if (!cancelled) setLoadingRetours(false); });
        return () => { cancelled = true; };
    }, [selectedFournisseur?.value, canEdit, reloadKey]);

    useEffect(() => {
        if (!canEdit || loadingRetours || retoursError || selectedDocuments.length === 0 || !formikRef.current) return;
        applyReturns(selectedTotal, availableRetours, formikRef.current.setFieldValue);
    }, [availableRetours, selectedTotal, selectedDocuments.length, canEdit, loadingRetours, retoursError]);

    const selectFournisseurFromDocument = (doc, setFieldValue) => {
        const fournisseurId = getDocumentFournisseurId(doc);
        const option = fournisseurOptions.find(item => String(item.value) === String(fournisseurId));
        if (option && !selectedFournisseur) {
            setSelectedFournisseur(option);
            setFieldValue('fournisseurId', option.value);
        }
        return option;
    };

    const toggleDocument = (doc, values, setFieldValue) => {
        const exists = selectedDocuments.some(item => item.id === doc.id);
        const next = exists
            ? selectedDocuments.filter(item => item.id !== doc.id)
            : [...selectedDocuments, doc];

        if (!exists && selectedDocuments.length > 0) {
            const currentFournisseurId = getDocumentFournisseurId(selectedDocuments[0]);
            if (String(getDocumentFournisseurId(doc)) !== String(currentFournisseurId)) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Attention',
                    text: 'Choisissez des documents du même fournisseur.',
                });
                return;
            }
        }

        const selectedOption = next.length > 0 ? selectFournisseurFromDocument(next[0], setFieldValue) : selectedFournisseur;
        if (selectedOption) {
            setFieldValue('fournisseurId', selectedOption.value);
        }

        const total = next.reduce((sum, item) => sum + getDocumentRemaining(item), 0);
        setSelectedDocuments(next);
        applyReturns(total, availableRetours, setFieldValue);
    };

    const handleFournisseurFilterChange = (option, setFieldValue) => {
        setSelectedFournisseur(option);
        setSelectedDocuments([]);
        setSelectedRetours([]);
        setFieldValue('fournisseurId', option?.value || '');
        setFieldValue('montantRegle', 0);
        setFieldValue('montantRetoursUtilises', 0);
        setFieldValue('montantPaiementReel', 0);
    };

    const handleSubmit = (values, { setSubmitting }) => {
        if (!canEdit || documentsLoading || loadingRetours || documentsError || retoursError) {
            setSubmitting(false);
            return;
        }
        if (selectedDocuments.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Attention',
                text: 'Cochez au moins un document à payer.',
            });
            setSubmitting(false);
            return;
        }

        const selectedFactures = typeReglement === 'FACTURE' ? selectedDocuments.map(doc => doc.numero).join(',') : '';
        const selectedBonReceptions = typeReglement === 'BON_RECEPTION' ? selectedDocuments.map(doc => doc.numero).join(',') : '';
        const payload = {
            data: {
                ...values,
                typeReglement,
                numFactures: selectedFactures,
                numBonReceptions: selectedBonReceptions,
                montantRegle: selectedTotal,
                montantRetoursUtilises: retoursTotal,
                montantPaiementReel: amountToPay,
                documentsIds: selectedDocuments.map(doc => doc.id),
                estAvance: false,
                utilisationAvances: [],
                utilisationRetours: selectedRetours.map(retour => ({
                    retourId: retour.retourId,
                    numeroRetour: retour.numeroRetour,
                    montantUtilise: toNumber(retour.montantUtilise),
                    commentaire: retour.commentaire || ''
                })),
            },
            onSuccess: () => {
                Swal.fire({ icon: 'success', title: 'Enregistré', text: 'Le paiement est enregistré.' });
                navigate(listPath);
            },
            onError: (error) => {
                Swal.fire({ icon: 'error', title: 'Paiement non enregistré', text: error?.message || 'Réessayez.' });
                setSubmitting(false);
            }
        };

        if (reglement) {
            dispatch(reglementsFournisseurActions.updateRequest({ ...payload, id: reglement.id }));
        } else {
            dispatch(reglementsFournisseurActions.createRequest(payload));
        }
    };

    const changeType = (type, setFieldValue) => {
        setTypeReglement(type);
        setSelectedDocuments([]);
        setSelectedRetours([]);
        setFieldValue('typeReglement', type);
        applyReturns(0, [], setFieldValue);
    };

    const renderDocuments = (values, setFieldValue, busy) => (
        <Card>
            <CardHeader><strong>1. Choisir les documents</strong><Badge color="primary">{selectedDocuments.length} coché(s)</Badge></CardHeader>
            <CardBody>
                <FormGroup>
                    <Label for="payment-supplier">Fournisseur *</Label>
                    <Select inputId="payment-supplier" options={fournisseurOptions} value={selectedFournisseur}
                        onChange={option => handleFournisseurFilterChange(option, setFieldValue)}
                        placeholder="Choisir un fournisseur" noOptionsMessage={() => 'Aucun fournisseur'}
                        isClearable={!reglement} isDisabled={!canEdit || busy}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
                    <ErrorMessage name="fournisseurId" component="div" className="text-danger small" />
                </FormGroup>
                {canEdit ? <>
                    <div className="payment-document-tools">
                        <div className="payment-type-buttons" role="group" aria-label="Type de documents">
                            {[['FACTURE', 'Factures'], ['BON_RECEPTION', 'Bons de réception']].map(([type, label]) => (
                                <Button key={type} type="button" color="primary" outline={typeReglement !== type} aria-pressed={typeReglement === type}
                                    disabled={busy} onClick={() => changeType(type, setFieldValue)}>{label}</Button>
                            ))}
                        </div>
                        <Input type="search" value={documentQuery} onChange={event => setDocumentQuery(event.target.value)}
                            placeholder="Chercher un numéro" aria-label="Chercher un document" />
                    </div>
                    <p className="small text-muted mb-2">Cochez les documents à payer.</p>
                    {documentsError ? <Alert color="danger">{documentsError} <Button type="button" size="sm" color="link" onClick={() => setReloadKey(key => key + 1)}>Réessayer</Button></Alert>
                        : documentsLoading ? <div role="status"><Spinner size="sm" /> Chargement des documents...</div>
                            : <div className="table-responsive payment-documents">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead><tr><th aria-label="Choisir" /><th>Document / Date</th>{!selectedFournisseur && <th>Fournisseur</th>}<th className="text-end">Reste à payer</th></tr></thead>
                                    <tbody>{filteredDocuments.map(doc => {
                                        const checked = selectedDocuments.some(item => item.id === doc.id);
                                        return <tr key={doc.id} className={checked ? 'payment-selected' : ''} onClick={() => !busy && toggleDocument(doc, values, setFieldValue)}>
                                            <td><Input type="checkbox" checked={checked} disabled={busy}
                                                aria-label={`Choisir ${doc.numero}`} onClick={event => event.stopPropagation()}
                                                onChange={() => toggleDocument(doc, values, setFieldValue)} /></td>
                                            <td><strong>{doc.numero}</strong><small className="d-block text-muted">{String(getDocumentDate(doc)).slice(0, 10)}</small></td>
                                            {!selectedFournisseur && <td>{getDocumentFournisseurName(doc)}</td>}
                                            <td className="text-end fw-semibold">{formatPrice(getDocumentRemaining(doc))}</td>
                                        </tr>;
                                    })}{!filteredDocuments.length && <tr><td colSpan={selectedFournisseur ? 3 : 4} className="text-center text-muted py-3">Aucun document à payer{documentQuery ? ' pour ce numéro' : ''}.</td></tr>}</tbody>
                                </Table>
                            </div>}
                </> : <div className="small"><strong>Documents concernés</strong><p className="mb-0 mt-1">{reglement.numFactures || reglement.numBonReceptions || 'Aucun document'}</p></div>}
            </CardBody>
        </Card>
    );

    const renderPayment = (values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur) => {
        const busy = isSubmitting || loading;
        const disabled = !canEdit || busy;
        const field = (name, label, type = 'text') => <FormGroup>
            <Label for={`payment-${name}`}>{label}</Label>
            <Field id={`payment-${name}`} name={name} type={type} disabled={disabled}
                className={`form-control ${errors[name] && touched[name] ? 'is-invalid' : ''}`} />
            <ErrorMessage name={name} component="div" className="invalid-feedback" />
        </FormGroup>;
        return <Card>
            <CardHeader><strong>2. Paiement</strong>{!canEdit && <Badge color="secondary">{reglement.statut === 'ANNULE' ? 'Annulé' : 'Enregistré'}</Badge>}</CardHeader>
            <CardBody>
                <div className="payment-summary mb-3">
                    {retoursTotal > 0 && <><div><span>Documents</span><strong>{formatPrice(selectedTotal)}</strong></div>
                        <div className="text-success"><span>Retours déduits</span><strong>− {formatPrice(retoursTotal)}</strong></div></>}
                    <div className="payment-total"><span>{canEdit ? 'À payer' : 'Montant du paiement'}</span><strong>{formatPrice(amountToPay)}</strong></div>
                </div>
                <Row className="g-2">
                    <Col sm={6}>{field('dateReglement', 'Date du paiement *', 'date')}</Col>
                    <Col sm={6}><FormGroup><Label for="payment-mode">Payer par</Label>
                        <Field as="select" id="payment-mode" name="modeReglement" className="form-control" disabled={disabled || amountToPay === 0}>
                            {modeReglementOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </Field><ErrorMessage name="modeReglement" component="div" className="text-danger small" /></FormGroup></Col>
                </Row>
                {values.modeReglement === 'CHEQUE' && amountToPay > 0 && <Row className="g-2">
                    <Col sm={6}>{field('numeroCheque', 'N° du chèque')}</Col>
                    <Col sm={6}><FormGroup><Label for="payment-bank">Banque</Label>
                        <Input id="payment-bank" type="select" name="banque" value={values.banque} onChange={handleChange} onBlur={handleBlur} disabled={disabled}>
                            <option value="">Choisir une banque</option>
                            {['BMCE', 'ATTIJARI', 'CIH', 'CREDIT DU MAROC', 'SG'].map(bank => <option key={bank}>{bank}</option>)}
                        </Input></FormGroup></Col>
                    <Col sm={6}>{field('dateEcheance', 'Date d’encaissement prévue', 'date')}</Col>
                </Row>}
                {values.modeReglement === 'VIREMENT' && amountToPay > 0 && field('referencePaiement', 'Référence du virement')}
                {loadingRetours && <div className="small text-muted mb-2" role="status"><Spinner size="sm" /> Vérification des retours...</div>}
                {retoursError && <Alert color="danger">{retoursError} <Button type="button" size="sm" color="link" onClick={() => setReloadKey(key => key + 1)}>Réessayer</Button></Alert>}
                {selectedRetours.length > 0 && <details className="payment-extra mb-2">
                    <summary>Retours déduits ({selectedRetours.length})</summary>
                    <p className="small text-muted my-2">Ces montants sont retirés du total à payer.</p>
                    <Table size="sm" className="mb-0"><thead><tr><th>Retour</th><th className="text-end">Montant utilisé</th></tr></thead>
                        <tbody>{selectedRetours.map(retour => <tr key={retour.retourId}><td>{retour.numeroRetour}</td><td>
                            <Input type="number" min="0" step="0.01" value={retour.montantUtilise} disabled={disabled}
                                aria-label={`Montant utilisé du retour ${retour.numeroRetour}`} onWheel={preventScroll}
                                onChange={event => {
                                    const otherTotal = selectedRetours.filter(item => item.retourId !== retour.retourId).reduce((sum, item) => sum + toNumber(item.montantUtilise), 0);
                                    const maximum = Math.max(Math.min(toNumber(retour.montantDisponible ?? retour.montantRetour), selectedTotal - otherTotal), 0);
                                    const next = selectedRetours.map(item => item.retourId === retour.retourId ? { ...item, montantUtilise: Math.max(0, Math.min(toNumber(event.target.value), maximum)) } : item);
                                    const used = next.reduce((sum, item) => sum + toNumber(item.montantUtilise), 0);
                                    setSelectedRetours(next);
                                    setFieldValue('montantRetoursUtilises', used);
                                    setFieldValue('montantPaiementReel', Math.max(selectedTotal - used, 0));
                                }} /></td></tr>)}</tbody>
                    </Table>
                </details>}
                <details className="payment-extra mb-3" open={values.commentaire ? true : undefined}>
                    <summary>Ajouter une note (facultatif)</summary>
                    <Field as="textarea" name="commentaire" rows="2" className="form-control mt-2" aria-label="Note" disabled={disabled} />
                </details>
                {canEdit && selectedDocuments.length > 0 && amountToPay === 0 && !loadingRetours && <p className="small text-success">Les retours couvrent tout le montant. Rien à verser.</p>}
                {canEdit && <p className="small text-muted">Enregistrez le paiement, puis confirmez-le dans la liste une fois l’argent versé.</p>}
                <div className="d-flex justify-content-end gap-2">
                    <Button type="button" color="secondary" outline disabled={busy} onClick={() => navigate(listPath)}>Retour</Button>
                    {canEdit && <Button type="submit" color="primary" disabled={busy || documentsLoading || loadingRetours || !!documentsError || !!retoursError || !selectedDocuments.length || !values.fournisseurId}>
                        {busy && <Spinner size="sm" className="me-2" />}Enregistrer
                    </Button>}
                </div>
            </CardBody>
        </Card>;
    };

    return <Container fluid className="supplier-payment-form">
        <div className="payment-heading"><div><h5>{reglement ? `Paiement ${reglement.reference || ''}` : 'Nouveau paiement fournisseur'}</h5>
            <p>Choisissez les documents, puis enregistrez le paiement.</p></div></div>
        <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => <Form>
                <Row className="g-2"><Col lg={7}>{renderDocuments(values, setFieldValue, isSubmitting || loading)}</Col>
                    <Col lg={5}>{renderPayment(values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur)}</Col></Row>
            </Form>}
        </Formik>
    </Container>;
};

export default ReglementFournisseurForm;
